// inv/src/components/investor-page/pInvestorProfile.jsx
import { useContext, useState, useEffect, useRef } from 'react';
import { API_URL } from '../../config';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { DataContext } from '../data/DataContext';
import { useTranslation } from 'react-i18next';
import InvestorDealsBlock from './modules/pDealsBlock';
import InvestorDealsChart from './modules/pDealsChart';
import InvestorLastInvestments from './modules/pLastInvestments';
import RelatedInvestors from './modules/pRelatedInvestors';
import CoInvestNetwork from './modules/pCoInvestNetwork';
import { fetchDealsBatchByInvestor } from './modules/pFetchDeals';
import SeoHead from '../../seo/seoHead';
import LoadingSpinner from '../ui/LoadingSpinner';

const PAGE_SIZE = 5;

const InvestorProfile = () => {
  const { investorId } = useParams();
  const { t, i18n } = useTranslation();

  const [listVersion, setListVersion] = useState(0);
  const [listReady, setListReady] = useState(false);

  const ssrData = useContext(DataContext);
  const ssrSeo = ssrData?.seo || {};
  const ssrInitialDeals = ssrData?.initialDeals;
  const ssrPrimaryIndustryStats = ssrData?.primaryIndustryStats || null;
  const ssrOtherTopIndustries = ssrData?.otherTopIndustries || [];
  const ssrIndustryMeta = ssrData?.industryMeta || null;
  const [investorData, setInvestorData] = useState(ssrData?.investorData || null);
  const [visibleDeals, setVisibleDeals] = useState(ssrInitialDeals?.visibleDeals || []);
  const [totalDealsCount, setTotalDealsCount] = useState(
    ssrInitialDeals?.totalDealsCount ?? 0
  );
  const [dealsToShow, setDealsToShow] = useState(
    ssrInitialDeals ? Number(ssrInitialDeals.dealsToShow || 0) : null
  );

  const _leadIds = Array.isArray(ssrData?.investorData?.as_lead_investor)
    ? ssrData.investorData.as_lead_investor.map(String)
    : [];
  const _partIds = Array.isArray(ssrData?.investorData?.as_investor_participated)
    ? ssrData.investorData.as_investor_participated.map(String)
    : [];
  const _leadSet = new Set(_leadIds);
  const _partSet = new Set(_partIds);
  let _both = 0;
  for (const id of _leadSet) if (_partSet.has(id)) _both++;
  const _leadOnly = _leadSet.size - _both;
  const _secondOnly = _partSet.size - _both;

  const [leadCount, setLeadCount] = useState(_leadOnly);
  const [participatedCount, setParticipatedCount] = useState(_secondOnly);
  const [bothCount, setBothCount] = useState(_both);

  const [hiddenMeta, setHiddenMeta] = useState(
    ssrInitialDeals?.hidden
      ? {
        page: ssrInitialDeals.hidden.page,
        perPage: ssrInitialDeals.hidden.perPage,
        pages: ssrInitialDeals.hidden.pages,
        total: ssrInitialDeals.hidden.total,
      }
      : { page: 1, perPage: 20, pages: 1, total: 0 }
  );
  const hiddenPagesCacheRef = useRef(new Map());

  const asLeadRef = useRef([]);
  const asPartRef = useRef([]);

  const [chartData, setChartData] = useState([]);

  const [primaryIndustryStats, setPrimaryIndustryStats] = useState(ssrPrimaryIndustryStats);
  const [otherTopIndustries, setOtherTopIndustries] = useState(ssrOtherTopIndustries);
  const [industryMeta, setIndustryMeta] = useState(ssrIndustryMeta);

  useEffect(() => {
    if (ssrInitialDeals) {
      setListVersion((v) => v + 1);
      setListReady(true);
    }
  }, [ssrInitialDeals]);

  useEffect(() => {
    setListReady(false);
    const investorIdFromUrl = investorId.split('-')[0];

    const fetchAll = async () => {
      try {
        const identifier = String(investorIdFromUrl).trim();
        const invUrl = `${API_URL}/investors-limited/${identifier}`;
        const invResp = await axios.get(invUrl, {
          params: { by: 'id' },
          withCredentials: true,
        });
        const inv = invResp.data || null;
        if (inv) {
          inv.as_lead_investor = Array.isArray(inv.as_lead_investor)
            ? inv.as_lead_investor
            : [];
          inv.as_investor_participated = Array.isArray(inv.as_investor_participated)
            ? inv.as_investor_participated
            : [];
          setInvestorData(inv);
        }

        const statsUrl = `${API_URL}/investors-limited/${identifier}/stats`;
        const statsResp = await axios.get(statsUrl, { params: { by: 'id' } });
        const st = statsResp.data || {};

        setBothCount(Number(st.both || 0));
        setLeadCount(Number(st.lead_only || 0));
        setParticipatedCount(Number(st.second_only || 0));
        setTotalDealsCount(Number(st.deals_total || 0));

        const years = Array.isArray(st.years) ? st.years : [];
        const chart = years.map((r) => ({
          year: String(r.y),
          allDeals: Number(r.all || 0),
          leadInvestor: Number(r.lead || 0),
          secondInvestor: Number(r.second || 0),
        }));
        setChartData(chart);

        try {
          const topUrl = `${API_URL}/investors-limited/${identifier}/top-industries`;
          const topResp = await axios.get(topUrl, { params: { by: 'id' } });
          const ti = topResp.data || {};
          setIndustryMeta({
            totalIndustries: Number(ti.total_industries || 0),
            topCount: Number(ti.top_count || (Array.isArray(ti.others) ? ti.others.length + (ti.primary ? 1 : 0) : 0)),
          });

          if (ti.primary) {
            const name = (
              ti.primary.industry ||
              ti.primary.industry_category ||
              ti.primary.name ||
              ''
            ).trim();

            const deals = Number(
              ti.primary.deals_total ?? ti.primary.deals ?? 0
            );
            const totalAmount = Number(ti.primary.investment_amount_total || 0);

            const totalDealsForInvestor = Number(st.deals_total || 0);
            const dealsSharePct =
              totalDealsForInvestor > 0 && deals > 0
                ? Math.round((deals / totalDealsForInvestor) * 100)
                : null;

            const avgTicket =
              deals > 0 && totalAmount > 0 ? totalAmount / deals : null;

            if (name) {
              setPrimaryIndustryStats({
                name,
                deals,
                dealsSharePct,
                totalAmount,
                avgTicket,
              });
            } else {
              setPrimaryIndustryStats(null);
            }
          } else {
            setPrimaryIndustryStats(null);
          }

          if (Array.isArray(ti.others)) {
            setOtherTopIndustries(
              ti.others
                .map((item) => {
                  if (!item) return '';
                  if (typeof item === 'string') return item.trim();

                  const name =
                    item.industry ||
                    item.industry_category ||
                    item.name ||
                    '';

                  return String(name).trim();
                })
                .filter(Boolean)
            );
          } else {
            setOtherTopIndustries([]);
          }
        } catch (e) {
          console.warn('Error fetching top industries:', e);
          setPrimaryIndustryStats(null);
          setOtherTopIndustries([]);
        }

        setListVersion((v) => v + 1);
      } catch (error) {
        console.error('Error fetching investor or stats:', error);
      } finally {
        setListReady(true);
      }
    };

    fetchAll();
  }, [investorId]);

  useEffect(() => {
    if (window.location.hash === '#last-investments') {
      const el = document.getElementById('last-investments');
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200);
      }
    }
  }, [listReady]);

  const enrich = (arr, asLead, asPart) =>
    (arr || []).map((d) => ({
      ...d,
      lead: asLead.includes(d._id),
      participated: asPart.includes(d._id),
    }));

  const buildSSRFirstPage = () => {
    if (!ssrInitialDeals) return null;
    const total = ssrInitialDeals.totalDealsCount || 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const visible = ssrInitialDeals.visibleDeals || [];
    const first = visible.slice(
      0,
      Math.min(PAGE_SIZE, ssrInitialDeals.dealsToShow || 0)
    );

    if (first.length < PAGE_SIZE) {
      const need = PAGE_SIZE - first.length;
      const redacted = (ssrInitialDeals.hidden?.items || [])
        .slice(0, need)
        .map((d) => ({
          ...d,
          redacted: true,
        }));
      return {
        items: [...first, ...redacted],
        totalPages,
        currentPage: 1,
      };
    }

    return {
      items: first,
      totalPages,
      currentPage: 1,
    };
  };
  const ssrFirstPage = buildSSRFirstPage();

  useEffect(() => {
    const loadDealsAndCounts = async () => {
      if (!investorData) {
        setVisibleDeals([]);
        setTotalDealsCount(0);
        setDealsToShow(null);
        setHiddenMeta({ page: 1, perPage: 20, pages: 1, total: 0 });
        setLeadCount(0);
        setParticipatedCount(0);
        setBothCount(0);
        setChartData([]);
        setListVersion((v) => v + 1);
        setListReady(false);
        return;
      }

      if (ssrInitialDeals) {
        const asLead = Array.isArray(investorData.as_lead_investor)
          ? investorData.as_lead_investor.map(String)
          : [];
        const asPart = Array.isArray(investorData.as_investor_participated)
          ? investorData.as_investor_participated.map(String)
          : [];
        asLeadRef.current = asLead;
        asPartRef.current = asPart;

        const leadSet = new Set(asLead);
        const partSet = new Set(asPart);
        let both = 0;
        for (const id of leadSet) if (partSet.has(id)) both++;

        setBothCount(both);
        setLeadCount(leadSet.size - both);
        setParticipatedCount(partSet.size - both);
        setTotalDealsCount(
          ssrInitialDeals.totalDealsCount ??
          leadSet.size + partSet.size - both
        );
        return;
      }

      const asLead = Array.isArray(investorData.as_lead_investor)
        ? investorData.as_lead_investor.map(String)
        : [];
      const asPart = Array.isArray(investorData.as_investor_participated)
        ? investorData.as_investor_participated.map(String)
        : [];
      asLeadRef.current = asLead;
      asPartRef.current = asPart;

      const leadSet = new Set(asLead);
      const partSet = new Set(asPart);
      let both = 0;
      for (const id of leadSet) if (partSet.has(id)) both++;
      const leadOnly = leadSet.size - both;
      const secondOnly = partSet.size - both;
      const totalUnique = leadSet.size + partSet.size - both;

      setBothCount(both);
      setLeadCount(leadOnly);
      setParticipatedCount(secondOnly);
      setTotalDealsCount(totalUnique);

      hiddenPagesCacheRef.current.clear();

      const allIds = [...asLead, ...asPart];
      if (allIds.length === 0) {
        setVisibleDeals([]);
        setDealsToShow(0);
        setHiddenMeta({ page: 1, perPage: 20, pages: 1, total: 0 });
        setChartData([]);
        setListVersion((v) => v + 1);
        setListReady(true);
        return;
      }

      const resp = await fetchDealsBatchByInvestor(investorData.id, {
        page: 1,
        perPage: 20,
        by: 'id',
      });

      setVisibleDeals(enrich(resp.visibleDeals, asLead, asPart));
      setDealsToShow(Number(resp.visibleCount || 0));
      setHiddenMeta({
        page: resp.hidden.page || 1,
        perPage: resp.hidden.perPage || PAGE_SIZE,
        pages: resp.hidden.pages || 1,
        total: resp.hidden.total || 0,
      });

      if ((resp.hidden?.items || []).length) {
        hiddenPagesCacheRef.current.set(
          resp.hidden.page,
          enrich(resp.hidden.items, asLead, asPart)
        );
      }

      setListVersion((v) => v + 1);
      requestAnimationFrame(() => setListReady(true));
    };

    loadDealsAndCounts();
  }, [investorData, ssrInitialDeals]);

  useEffect(() => {
    const buildChart = async () => {
      if (!investorData || totalDealsCount === 0) {
        setChartData([]);
        return;
      }
      if (chartData && chartData.length > 0) return;

      const map = new Map();
      let minYear = Infinity,
        maxYear = -Infinity;

      const inc = (y, lead, part) => {
        const k = String(y);
        if (!map.has(k)) {
          map.set(k, {
            year: k,
            allDeals: 0,
            leadInvestor: 0,
            secondInvestor: 0,
          });
        }
        const row = map.get(k);
        row.allDeals += 1;
        if (lead) row.leadInvestor += 1;
        if (part) row.secondInvestor += 1;
      };

      (visibleDeals || []).forEach((d) => {
        const y = new Date(d.announcement_date_dt).getFullYear();
        if (!Number.isFinite(y)) return;
        inc(y, d.lead, d.participated);
        if (y < minYear) minYear = y;
        if (y > maxYear) maxYear = y;
      });

      if (!Number.isFinite(minYear) || !Number.isFinite(maxYear)) {
        setChartData([]);
        return;
      }

      const arr = [];
      for (let y = minYear; y <= maxYear; y++) {
        arr.push(
          map.get(String(y)) || {
            year: String(y),
            allDeals: 0,
            leadInvestor: 0,
            secondInvestor: 0,
          }
        );
      }
      setChartData(arr);
    };

    buildChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorData, visibleDeals, totalDealsCount]);

  const getUnifiedPage = async (page) => {
    if (dealsToShow === null || visibleDeals.length === 0) {
      return { items: [], totalPages: 1, currentPage: 1 };
    }

    const total = totalDealsCount;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(Math.max(1, page), totalPages);

    const effectiveVisible = Math.min(dealsToShow, visibleDeals.length);

    const startIdx = (safePage - 1) * PAGE_SIZE;
    const endIdx = Math.min(startIdx + PAGE_SIZE, total);

    const items = [];

    for (let gi = startIdx; gi < endIdx; gi++) {
      if (gi < effectiveVisible) {
        const item = visibleDeals[gi];
        if (item)
          items.push({
            ...item,
            redacted: false,
          });
        continue;
      }

      const h = gi - dealsToShow;
      if (h < 0) {
        continue;
      }

      const serverPage = Math.floor(h / hiddenMeta.perPage) + 1;
      const offsetInServerPage = h % hiddenMeta.perPage;

      let serverPageItems = hiddenPagesCacheRef.current.get(serverPage);
      if (!serverPageItems) {
        const resp = await fetchDealsBatchByInvestor(investorData.id, {
          page: serverPage,
          perPage: hiddenMeta.perPage,
          by: 'id',
        });

        const enriched = (resp.hidden?.items || []).map((d) => ({
          ...d,
          lead: asLeadRef.current.includes(d._id),
          participated: asPartRef.current.includes(d._id),
        }));

        hiddenPagesCacheRef.current.set(resp.hidden.page, enriched);

        setHiddenMeta((prev) => ({
          page: resp.hidden.page ?? prev.page,
          perPage: resp.hidden.perPage ?? prev.perPage,
          pages: resp.hidden.pages ?? prev.pages,
          total: resp.hidden.total ?? prev.total,
        }));

        serverPageItems = enriched;
      }

      const red = serverPageItems[offsetInServerPage];
      if (red)
        items.push({
          ...red,
          redacted: true,
        });
    }

    return { items, totalPages, currentPage: safePage };
  };

  if (!investorData) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <SeoHead
        lang={i18n.language}
        title={ssrSeo.title}
        description={ssrSeo.description}
        canonicalUrl={ssrSeo.canonicalUrl}
        ogTitle={ssrSeo.ogTitle}
        ogDescription={ssrSeo.ogDescription}
        ogImage={ssrSeo.ogImage}
        ogUrl={ssrSeo.ogUrl}
        ogType={ssrSeo.ogType}
        twitterCard={ssrSeo.twitterCard}
        robots={ssrSeo.robots}
        alternates={ssrSeo.alternates}
        jsonLd={ssrSeo.jsonLd}
      />
      <div className="container mx-auto">
        <div className="flex flex-col mt-4">
          <InvestorDealsBlock
            investorData={investorData}
            totalDeals={totalDealsCount}
            leadCount={leadCount}
            participatedCount={participatedCount}
            bothCount={bothCount}
            primaryIndustryStats={primaryIndustryStats}
            otherTopIndustries={otherTopIndustries}
            industryMeta={industryMeta}
          />

          <div className="w-full rounded-md mb-6 bg-white p-4 shadow">
            <InvestorDealsChart data={chartData} />
          </div>

          <div className="w-full mb-6">
            <CoInvestNetwork investorId={investorData?.id} />
          </div>

          <div id="last-investments" className="w-full rounded-md bg-white p-6 shadow">
            <InvestorLastInvestments
              totalDeals={totalDealsCount}
              visibleCount={dealsToShow}
              getUnifiedPage={getUnifiedPage}
              listVersion={listVersion}
              initialized={
                Boolean(ssrInitialDeals) || (listReady && dealsToShow !== null)
              }
              ssrFirstPage={ssrFirstPage}
            />
          </div>

          <div className="w-full mt-6">
            <RelatedInvestors
              investorId={investorData?.id}
              currentCountry={investorData?.country}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default InvestorProfile;
